import { useClient } from '@/context/ClientContext'
import { getStepsRefetchInterval } from '@/lib/odmStages'
import { type UseQueryResult, useQuery } from '@tanstack/react-query'

import type { MappingJobStep } from '@droidmap/mapping-job-service-contract'
import type { DroidMapMetadata } from '@droidmap/shared-service-contract'

/** How often to re-poll a job's steps while it is still running. */
const POLL_INTERVAL_MS = 5_000

/**
 * Fetch a mapping job's per-stage steps, polling while the job is active.
 *
 * The query re-fetches every {@link POLL_INTERVAL_MS} until the steps reach a
 * terminal state (all completed, or one failed) — see
 * {@link getStepsRefetchInterval} — then goes quiet. Disabled when no
 * `mappingJobId` is available (e.g. a non-photogrammetry task).
 */
export const useMappingJobSteps = (
  mappingJobId: string | undefined,
  metadata: DroidMapMetadata,
): UseQueryResult<MappingJobStep[]> => {
  const { mappingJobClient } = useClient()

  return useQuery<MappingJobStep[]>({
    queryKey: ['mappingJobSteps', mappingJobId],
    enabled: !!mappingJobId,
    queryFn: () =>
      mappingJobClient.getMappingJobSteps(
        { mappingJobId: mappingJobId as string },
        metadata,
      ),
    refetchInterval: (query) =>
      getStepsRefetchInterval(query.state.data, POLL_INTERVAL_MS),
    refetchIntervalInBackground: false,
    // One retry only — the 5s poll already re-attempts; stacked retries made
    // a persistent failure take ~35s to surface.
    retry: 1,
  })
}
